<template>
  <div class="su-modal-overlay" @click.self="$emit('close')">
    <div class="su-modal-dialog" role="dialog" aria-modal="true">
      <div class="su-modal-content">
        <div class="su-modal-header">
          <div>
            <h5 class="su-modal-title">Select Brand</h5>
            <p class="su-modal-subtitle mb-0">
              Alege providerul, apoi selectează unul sau mai multe branduri și apasă Save.
            </p>
          </div>

          <button
            type="button"
            class="su-close-btn"
            aria-label="Close"
            @click="$emit('close')"
          >
            ×
          </button>
        </div>

        <div class="su-modal-topbar">
          <div class="su-modal-topbar__steps">
            <div class="su-step" :class="{ active: step === 1, done: step > 1 }">
              <span class="su-step-index">1</span>
              <span class="su-step-label">Provider</span>
            </div>

            <div class="su-step-line"></div>

            <div class="su-step" :class="{ active: step === 2 }">
              <span class="su-step-index">2</span>
              <span class="su-step-label">Branduri</span>
            </div>
          </div>

          <div class="su-modal-topbar__provider" v-if="step === 2 && selectedProvider">
            <div class="su-provider-inline">
              <div class="su-provider-inline__label">Provider selectat</div>
              <div class="su-provider-inline__name">
                {{ selectedProvider ? selectedProvider.name : '-' }}
              </div>
            </div>

            <button
              type="button"
              class="su-cancel-btn"
              @click="backToProviders"
            >
              Schimbă provider
            </button>
          </div>
        </div>

        <div class="su-modal-body">
          <div v-if="loading" class="su-loading-state">
            <i class="bi bi-arrow-repeat spin"></i>
            <span>{{ $t('loading') }}</span>
          </div>

          <div v-else-if="step === 1">
            <div class="su-provider-header">
              <div>
                <h6 class="mb-1">Alege providerul</h6>
                <small class="text-muted">
                  Selectează providerul pentru a încărca brandurile disponibile.
                </small>
              </div>
            </div>

            <div v-if="!providers.length" class="su-placeholder-state">
              <div class="su-placeholder-box">
                <div class="su-placeholder-title">
                  Nu există provideri
                </div>
                <div class="su-placeholder-text">
                  Lista de provideri este goală momentan.
                </div>
              </div>
            </div>

            <div v-else class="su-provider-grid">
              <button
                v-for="provider in providers"
                :key="'provider_' + provider.id"
                type="button"
                class="su-provider-card"
                @click="selectProvider(provider)"
              >
                <div class="su-provider-card__content">
                  <div class="su-provider-card__title">
                    {{ provider.name }}
                  </div>
                  <div class="su-provider-card__meta">
                    ID: {{ provider.id }}
                  </div>
                </div>
                <div class="su-provider-card__arrow">→</div>
              </button>
            </div>
          </div>

          <div v-else-if="step === 2">
            <div class="su-results-card">
              <div class="su-results-header">
                <div class="su-results-header__left">
                  <div class="su-results-title-wrap">
                    <h6 class="mb-0">Branduri disponibile</h6>

                    <span v-if="selectedItems.length" class="su-selected-pill">
                      {{ selectedItems.length }} selectate
                    </span>

                    <button
                      v-if="selectedItems.length"
                      type="button"
                      class="su-clear-inline-btn"
                      @click="clearSelection"
                    >
                      Clear all
                    </button>
                  </div>
                </div>

                <div class="su-results-header__right">
                  <input
                    v-model="searchQuery"
                    type="text"
                    class="su-search-input su-search-input--compact"
                    :placeholder="$t('search') + '…'"
                    aria-label="Search brands"
                  >

                  <div class="su-results-counter" v-if="pagination.total">
                    {{ pageStart }}–{{ pageEnd }} / {{ pagination.total }}
                  </div>
                </div>
              </div>

              <div class="table-responsive">
                <table class="table su-table align-middle mb-0">
                  <thead>
                  <tr>
                    <th style="width: 60px;" class="text-center">
                      <input
                        type="checkbox"
                        :checked="allCurrentPageSelected"
                        :indeterminate.prop="someCurrentPageSelected && !allCurrentPageSelected"
                        @change="toggleSelectAllCurrentPage"
                      >
                    </th>
                    <th style="width: 70%;">{{ $t('superUpLang.widget.modal.name') }}</th>
                    <th style="width: 20%;" class="text-end">Status</th>
                  </tr>
                  </thead>

                  <tbody>
                  <tr
                    v-for="p in products"
                    :key="p.id"
                    :class="{ 'su-row-selected': isSelected(p) }"
                  >
                    <td class="text-center">
                      <input
                        type="checkbox"
                        :checked="isSelected(p)"
                        @change="toggleItem(p)"
                      >
                    </td>

                    <td>
                      <div class="su-product-cell">
                        <div class="su-product-name">{{ p.name }}</div>
                        <div class="su-product-meta">ID: {{ p.id }}</div>
                      </div>
                    </td>

                    <td class="text-end">
                        <span v-if="isSelected(p)" class="su-badge-selected">
                          Selected
                        </span>
                      <button
                        v-else
                        class="su-select-btn"
                        @click="toggleItem(p)"
                      >
                        {{ $t('select') }}
                      </button>
                    </td>
                  </tr>

                  <tr v-if="!products.length">
                    <td colspan="3" class="text-center py-5">
                      <div class="su-empty-state">
                        <div class="su-empty-title">{{ $t('noResults') }}</div>
                        <div class="su-empty-text">
                          Încearcă un alt termen de căutare sau schimbă providerul.
                        </div>
                      </div>
                    </td>
                  </tr>
                  </tbody>
                </table>
              </div>

              <nav
                v-if="pagination.total > pagination.per_page"
                aria-label="Products pagination"
                class="su-pagination-wrap"
              >
                <div class="su-pagination-info">
                  {{ pageStart }}–{{ pageEnd }} / {{ pagination.total }}
                </div>

                <ul class="pagination mb-0">
                  <li class="page-item" :class="{ disabled: pagination.current_page === 1 }">
                    <button
                      class="page-link"
                      @click="goToPage(pagination.current_page - 1)"
                      :disabled="pagination.current_page === 1"
                    >
                      «
                    </button>
                  </li>

                  <li
                    v-for="n in pageNumbers"
                    :key="'p' + n"
                    class="page-item"
                    :class="{ active: n === pagination.current_page }"
                  >
                    <button class="page-link" @click="goToPage(n)">
                      {{ n }}
                    </button>
                  </li>

                  <li
                    class="page-item"
                    :class="{ disabled: pagination.current_page === pagination.last_page }"
                  >
                    <button
                      class="page-link"
                      @click="goToPage(pagination.current_page + 1)"
                      :disabled="pagination.current_page === pagination.last_page"
                    >
                      »
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        </div>

        <div class="su-modal-footer">
          <div class="su-footer-left">
            <span v-if="selectedItems.length" class="su-footer-count">
              {{ selectedItems.length }} selectate
            </span>
          </div>

          <div class="su-footer-actions">
            <button type="button" class="su-cancel-btn" @click="$emit('close')">
              {{ $t('cancel') }}
            </button>
            <button
              type="button"
              class="su-save-btn"
              :disabled="!selectedItems.length"
              @click="saveSelection"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import HttpModalResourcesServices from "@/superUp-module/resources/services/httpModalResourcesServices";

export default {
  name: "BrandsSelectorModal",
  props: {
    type: { type: Number, default: 1, required: true },
    initialProviderId: { type: [String, Number], default: null },
    initialSelected: { type: Array, default: () => [] }
  },
  data() {
    return {
      step: 1,
      providers: [],
      selectedProvider: null,
      products: [],
      loading: false,
      searchQuery: "",
      searchDebounceHandle: 0,
      pagination: { current_page: 1, last_page: 1, per_page: 15, total: 0 },
      selectedItemsMap: {}
    };
  },
  computed: {
    selectedItems() {
      return Object.values(this.selectedItemsMap);
    },
    pageNumbers() {
      const total = this.pagination.last_page || 1;
      const cur = this.pagination.current_page || 1;
      const span = 7;
      let start = Math.max(1, cur - Math.floor(span / 2));
      let end = Math.min(total, start + span - 1);
      start = Math.max(1, end - span + 1);
      const arr = [];
      for (let i = start; i <= end; i++) arr.push(i);
      return arr;
    },
    pageStart() {
      const { current_page, per_page, total } = this.pagination;
      if (!total) return 0;
      return (current_page - 1) * per_page + 1;
    },
    pageEnd() {
      const { current_page, per_page, total } = this.pagination;
      return Math.min(current_page * per_page, total);
    },
    allCurrentPageSelected() {
      return this.products.length > 0 && this.products.every(item => this.isSelected(item));
    },
    someCurrentPageSelected() {
      return this.products.some(item => this.isSelected(item));
    }
  },
  watch: {
    searchQuery() {
      if (this.searchDebounceHandle) clearTimeout(this.searchDebounceHandle);
      this.searchDebounceHandle = setTimeout(() => {
        if (this.step === 2) {
          this.fetchProducts(1);
        }
      }, 300);
    }
  },
  methods: {
    async fetchProviders() {
      try {
        this.loading = true;
        const r = await HttpModalResourcesServices.getProviders(this.type);
        this.providers = r.data || [];

        if (this.initialProviderId) {
          const pre = this.providers.find(
            p => String(p.id) === String(this.initialProviderId)
          );

          if (pre) {
            await this.selectProvider(pre);
          }
        }
      } finally {
        this.loading = false;
      }
    },

    async fetchProducts(page = 1) {
      if (!this.selectedProvider) return;

      try {
        this.loading = true;
        const r = await HttpModalResourcesServices.getBrands({
          type: this.type,
          provider_id: this.selectedProvider.id,
          page,
          search: this.searchQuery || undefined
        });

        const data = r.data || {};
        this.products = data.data || [];

        const meta = data.meta || data;
        this.pagination.current_page = +meta.current_page || 1;
        this.pagination.last_page = +meta.last_page || 1;
        this.pagination.per_page = +meta.per_page || (this.products.length || 15);
        this.pagination.total = +meta.total || this.products.length;
      } catch {
        this.products = [];
        this.pagination = { current_page: 1, last_page: 1, per_page: 15, total: 0 };
      } finally {
        this.loading = false;
      }
    },

    async selectProvider(provider) {
      this.selectedProvider = provider;
      this.step = 2;
      this.searchQuery = "";
      await this.fetchProducts(1);
    },

    backToProviders() {
      this.step = 1;
      this.selectedProvider = null;
      this.products = [];
      this.searchQuery = "";
      this.pagination = { current_page: 1, last_page: 1, per_page: 15, total: 0 };
    },

    goToPage(n) {
      if (n < 1 || n > this.pagination.last_page || n === this.pagination.current_page) return;
      this.fetchProducts(n);
    },

    isSelected(item) {
      return !!this.selectedItemsMap[item.id];
    },

    toggleItem(item) {
      if (this.isSelected(item)) {
        this.$delete(this.selectedItemsMap, item.id);
      } else {
        this.$set(this.selectedItemsMap, item.id, item);
      }
    },

    toggleSelectAllCurrentPage(event) {
      const checked = event.target.checked;

      if (checked) {
        this.products.forEach(item => {
          if (!this.isSelected(item)) {
            this.$set(this.selectedItemsMap, item.id, item);
          }
        });
      } else {
        this.products.forEach(item => {
          if (this.isSelected(item)) {
            this.$delete(this.selectedItemsMap, item.id);
          }
        });
      }
    },

    clearSelection() {
      this.selectedItemsMap = {};
    },

    saveSelection() {
      this.$emit("select", this.selectedItems);
    },

    hydrateInitialSelected() {
      if (!Array.isArray(this.initialSelected) || !this.initialSelected.length) return;

      const map = {};
      this.initialSelected.forEach(item => {
        if (item && item.id) {
          map[item.id] = item;
        }
      });
      this.selectedItemsMap = map;
    }
  },
  mounted() {
    this.hydrateInitialSelected();
    this.fetchProviders();
  }
};
</script>

<style scoped>
.su-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(15, 23, 42, 0.48);
  backdrop-filter: blur(3px);
}

.su-modal-dialog {
  width: 100%;
  max-width: 1080px;
  max-height: 90vh;
}

.su-modal-content {
  background: #ffffff;
  border-radius: 22px;
  box-shadow: 0 24px 80px rgba(15, 23, 42, 0.24);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: 90vh;
}

.su-modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 20px 14px;
  border-bottom: 1px solid #edf2f7;
  background: linear-gradient(180deg, #f8fbff 0%, #ffffff 100%);
}

.su-modal-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
  color: #111827;
}

.su-modal-subtitle {
  margin-top: 6px;
  color: #6b7280;
  font-size: 0.92rem;
}

.su-close-btn {
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 12px;
  background: #f3f4f6;
  color: #374151;
  font-size: 28px;
  line-height: 1;
  cursor: pointer;
  transition: all .2s ease;
  flex-shrink: 0;
}

.su-close-btn:hover {
  background: #e5e7eb;
}

.su-modal-topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  padding: 10px 20px;
  border-bottom: 1px solid #edf2f7;
  background: #fff;
}

.su-modal-topbar__steps {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.su-modal-topbar__provider {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  min-width: 0;
  flex: 1;
}

.su-provider-inline {
  min-width: 0;
  text-align: right;
}

.su-provider-inline__label {
  font-size: 0.74rem;
  text-transform: uppercase;
  letter-spacing: .04em;
  color: #98a2b3;
  font-weight: 700;
}

.su-provider-inline__name {
  font-size: 0.95rem;
  font-weight: 700;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 260px;
}

.su-step {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  color: #98a2b3;
  font-weight: 700;
}

.su-step.active,
.su-step.done {
  color: #3559e0;
}

.su-step-index {
  width: 30px;
  height: 30px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #eef2f7;
  color: inherit;
  font-size: 0.86rem;
}

.su-step.active .su-step-index,
.su-step.done .su-step-index {
  background: #3559e0;
  color: #fff;
}

.su-step-line {
  width: 30px;
  height: 2px;
  background: #e5e7eb;
}

.su-modal-body {
  padding: 12px 16px;
  overflow: auto;
  flex: 1;
  background: #fcfdff;
}

.su-provider-header,
.su-results-card,
.su-placeholder-box {
  background: #ffffff;
  border: 1px solid #e8eef5;
  border-radius: 18px;
  box-shadow: 0 10px 30px rgba(16, 24, 40, 0.04);
}

.su-provider-header {
  padding: 14px 16px;
  margin-bottom: 12px;
}

.su-provider-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 16px;
}

.su-provider-card {
  border: 1px solid #e5eaf1;
  background: #fff;
  border-radius: 18px;
  padding: 18px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  text-align: left;
  cursor: pointer;
  transition: all .2s ease;
  box-shadow: 0 8px 24px rgba(16, 24, 40, 0.04);
}

.su-provider-card:hover {
  transform: translateY(-2px);
  border-color: #cfdcf7;
  background: #fafcff;
  box-shadow: 0 14px 32px rgba(53, 89, 224, 0.08);
}

.su-provider-card__title {
  font-size: 1rem;
  font-weight: 700;
  color: #111827;
}

.su-provider-card__meta {
  margin-top: 4px;
  font-size: 0.82rem;
  color: #98a2b3;
}

.su-provider-card__arrow {
  color: #3559e0;
  font-size: 1.2rem;
  font-weight: 700;
}

.su-search-input {
  min-width: 240px;
  width: 100%;
  height: 40px;
  border: 1px solid #d0d7e2;
  border-radius: 12px;
  padding: 0 14px;
  font-size: 0.95rem;
  outline: none;
  transition: all .2s ease;
  background: #fff;
}

.su-search-input:focus {
  border-color: #7c9cff;
  box-shadow: 0 0 0 4px rgba(124, 156, 255, 0.16);
}

.su-search-input--compact {
  width: 240px;
  min-width: 240px;
}

.su-loading-state {
  min-height: 220px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: #475467;
  font-weight: 600;
}

.spin {
  animation: spin 1s linear infinite;
}

.su-results-card {
  overflow: hidden;
}

.su-results-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border-bottom: 1px solid #edf2f7;
  background: #fff;
}

.su-results-header__left {
  display: flex;
  align-items: center;
  min-width: 0;
}

.su-results-title-wrap {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.su-results-header__right {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.su-selected-pill {
  display: inline-flex;
  align-items: center;
  padding: 6px 10px;
  border-radius: 999px;
  background: #eef4ff;
  color: #2748c7;
  font-size: 0.82rem;
  font-weight: 700;
  white-space: nowrap;
}

.su-clear-inline-btn {
  border: none;
  background: transparent;
  color: #3559e0;
  font-weight: 700;
  font-size: 0.85rem;
  cursor: pointer;
  padding: 0;
}

.su-results-counter {
  background: #f4f7fb;
  color: #475467;
  padding: 8px 12px;
  border-radius: 999px;
  font-size: 0.84rem;
  font-weight: 700;
  white-space: nowrap;
}

.su-table {
  margin-bottom: 0;
}

.su-table thead th {
  background: #f8fafc;
  color: #475467;
  font-size: 0.84rem;
  font-weight: 700;
  padding: 12px 14px;
  border-bottom: 1px solid #e6edf5;
  border-top: none;
}

.su-table tbody td {
  padding: 12px 14px;
  border-color: #f0f4f8;
  vertical-align: middle;
  background: #fff;
}

.su-table tbody tr:hover td {
  background: #fafcff;
}

.su-row-selected td {
  background: #f5f9ff !important;
}

.su-product-cell {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.su-product-name {
  font-weight: 700;
  color: #111827;
}

.su-product-meta {
  font-size: 0.82rem;
  color: #98a2b3;
}

.su-select-btn {
  border: none;
  background: #3559e0;
  color: #fff;
  padding: 8px 12px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all .2s ease;
}

.su-select-btn:hover {
  background: #2748c7;
}

.su-badge-selected {
  display: inline-flex;
  align-items: center;
  padding: 7px 10px;
  border-radius: 999px;
  background: #e8f7ee;
  color: #117a37;
  font-size: 0.82rem;
  font-weight: 700;
}

.su-empty-state {
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: center;
  justify-content: center;
}

.su-empty-title {
  font-weight: 700;
  color: #344054;
}

.su-empty-text {
  color: #667085;
  font-size: 0.92rem;
}

.su-pagination-wrap {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  padding: 12px 14px 14px;
  border-top: 1px solid #edf2f7;
  background: #fff;
}

.su-pagination-info {
  font-size: 0.88rem;
  color: #667085;
  font-weight: 600;
}

.su-placeholder-state {
  min-height: 220px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.su-placeholder-box {
  width: 100%;
  max-width: 520px;
  padding: 30px 24px;
  text-align: center;
}

.su-placeholder-title {
  font-size: 1rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 8px;
}

.su-placeholder-text {
  color: #667085;
  font-size: 0.94rem;
}

.su-modal-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  padding: 12px 20px;
  border-top: 1px solid #edf2f7;
  background: #fff;
}

.su-footer-left {
  min-height: 24px;
  display: flex;
  align-items: center;
}

.su-footer-count {
  font-size: 0.9rem;
  color: #475467;
  font-weight: 600;
}

.su-footer-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.su-cancel-btn {
  border: 1px solid #d0d7e2;
  background: #fff;
  color: #374151;
  padding: 9px 14px;
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  transition: all .2s ease;
}

.su-cancel-btn:hover {
  background: #f8fafc;
}

.su-save-btn {
  border: none;
  background: #3559e0;
  color: #fff;
  padding: 9px 16px;
  border-radius: 10px;
  font-weight: 700;
  cursor: pointer;
  transition: all .2s ease;
}

.su-save-btn:hover {
  background: #2748c7;
}

.su-save-btn:disabled {
  opacity: .5;
  cursor: not-allowed;
}

:deep(.page-link) {
  border-radius: 10px !important;
  margin: 0 2px;
  border: 1px solid #d8e1eb;
  color: #344054;
  min-width: 38px;
  text-align: center;
}

:deep(.page-item.active .page-link) {
  background: #3559e0;
  border-color: #3559e0;
  color: #fff;
}

:deep(.page-item.disabled .page-link) {
  background: #f8fafc;
  color: #98a2b3;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

@media (max-width: 991px) {
  .su-modal-overlay {
    padding: 12px;
  }

  .su-modal-dialog,
  .su-modal-content {
    max-height: 94vh;
  }

  .su-modal-header,
  .su-modal-topbar,
  .su-modal-body,
  .su-modal-footer {
    padding-left: 14px;
    padding-right: 14px;
  }

  .su-modal-topbar,
  .su-results-header,
  .su-pagination-wrap,
  .su-modal-footer {
    flex-direction: column;
    align-items: flex-start;
  }

  .su-modal-topbar__provider {
    width: 100%;
    justify-content: space-between;
  }

  .su-provider-inline {
    text-align: left;
  }

  .su-provider-inline__name {
    max-width: 100%;
    white-space: normal;
  }

  .su-results-header__right {
    width: 100%;
    justify-content: flex-start;
  }

  .su-search-input--compact {
    width: 100%;
    min-width: 100%;
  }

  .su-footer-actions {
    width: 100%;
  }

  .su-provider-grid {
    grid-template-columns: 1fr;
  }
}
</style>
